import { Container, Graphics, MIPMAP_MODES, Sprite, Text, TextMetrics, TextStyle } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Callback } from "game-common/util"
import { COLORS, INTRO_FONT } from "./intro_theme"
import { choreoMs, realMs } from "./intro_pace"
import {
    FLIGHT_DELAY,
    easeOutCubic,
    easeOutExpo,
    flightRuleProgressAt,
    flightSettleTs,
    flightStateAt,
    letterStateAt,
    rampColor,
    ruleProgressAt,
    settleTs,
} from "./logo_choreography"

const LOGO_SIZE = 88
/** Wide tracking -- roughly 0.16em, applied between letters (never after the last one). */
const TRACKING = 14

/** Offset dark copy behind each glyph. The canopy underneath is busy and nearly the same value as
 *  the accent, so without this the wordmark has no edge to sit on. Matches drawPanel's shadow. */
const SHADOW_OFFSET_X = 2
const SHADOW_OFFSET_Y = 5
const SHADOW_ALPHA = 0.4

/** The halo is a scaled copy of the glyph behind it -- never a filter, see intro_theme. */
const GLOW_SPREAD = 1.07

/** The hairline under the wordmark: half-heights at its thickest point, in logical pixels. */
const RULE_WIDTH_RATIO = 0.56
const RULE_CORE_HALF = 1.0
const RULE_GLOW_HALF = 3.0
const RULE_GAP = 16

/** The loading screen's wordmark colour (index.html `.loading-brand`): where a flight starts. */
const LOADING_CREAM = 0xffedc7

interface Letter {
    container: Container
    glyph: Text
    glow: Sprite
    shadow: Sprite
    width: number
    /** How far below the letter's centre its baseline sits, at scale 1. */
    baseline: number
}

/** Where one letter of the loading screen's wordmark is, in the wordmark's own coordinates. */
export interface FlightStart {
    /** Centre of the letter's advance. */
    x: number
    baseline: number
    /** Font size, in the same units. */
    size: number
}

export interface FlightOptions {
    from: FlightStart[]
    /** The first frame the letters move: the HTML ones have to go on that same frame. */
    onLiftOff?: () => void
}

interface Flight {
    from: { x: number; y: number; scale: number }[]
    onLiftOff?: () => void
    liftedOff: boolean
}

export class ProjectLogo extends Graphics {
    logoText: string = "samsara"

    private letters: Letter[] = []
    private letterWidths: number[] = []
    private restX: number[] = []
    private flight?: Flight
    private totalWidth: number = 0
    private lineHeight: number = 0

    private rule?: Graphics
    private startedAt: number = 0
    private pulsedAt?: number
    private frameHandle?: number
    private settleHandle?: any
    private disposed: boolean = false

    constructor() {
        super()
        this.build()
    }

    /**
     * Build every letter up front and cache the run's width.
     *
     * The width has to be exact from frame 1: `intro.ts` centres the wordmark with it once, and
     * the old version both hardcoded `calcWidth() => 630` against a run that rendered ~427 wide
     * AND appended letters one per timer tick, so the container grew while it was spelling itself
     * out and anything positioned from it drifted.
     */
    private build = () => {
        this.letters = this.logoText.split("").map(this.buildLetter)
        this.letterWidths = this.letters.map(letter => letter.width)
        this.lineHeight = this.letters.length > 0 ? this.letters[0].glyph.height : 0
        this.totalWidth =
            this.letterWidths.reduce((sum, w) => sum + w, 0) + TRACKING * Math.max(0, this.letterWidths.length - 1)
    }

    private renderLetter = (letter: string): Text => {
        const text = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .fontSize(LOGO_SIZE)
            .color(0xffffff)
            // The default 1px black outline is for labels out in the world. Here it only muddies
            // the thin Jost 300 stems; the shadow copy below does the separation properly.
            .strokeThickness(0)
            .render(letter) as Text
        // Set before anything reads .width: a style change re-rasterizes, and Jost 300 and 400 have
        // different advances, so measuring at the default weight would mis-size the run.
        text.style.fontWeight = "300"
        return text
    }

    /**
     * One letter = three draws of the SAME raster. Reading `.width` forces the Text to rasterize
     * (the getter calls updateText), after which its texture can back plain Sprites for the shadow
     * and the halo -- 7 canvas textures for the wordmark instead of 21, and the two copies can
     * never drift out of register with the glyph.
     */
    private buildLetter = (letter: string): Letter => {
        const glyph = this.renderLetter(letter)
        glyph.anchor.set(0.5)
        // Reading .width is also what forces the raster -- the getter calls updateText -- and
        // everything below depends on the texture being final.
        const width = glyph.width

        // pixi draws a line's baseline at padding + half the stroke + the font's ascent from the top
        // of the raster; anchored at 0.5, the top is half the height above the centre.
        const style = glyph.style as TextStyle
        const metrics = TextMetrics.measureText(letter, style)
        const baseline = -glyph.height / 2 + (style.padding || 0) + (style.strokeThickness || 0) / 2 + metrics.fontProperties.ascent

        const shadow = new Sprite(glyph.texture)
        shadow.anchor.set(0.5)
        shadow.tint = COLORS.shadow
        shadow.alpha = SHADOW_ALPHA
        shadow.x = SHADOW_OFFSET_X
        shadow.y = SHADOW_OFFSET_Y

        const glow = new Sprite(glyph.texture)
        glow.anchor.set(0.5)
        glow.tint = COLORS.accent
        glow.scale.set(GLOW_SPREAD)
        glow.alpha = 0

        const container = new Container()
        // Anchored glyphs mean the container's origin is the letter's centre, so the pop scales
        // about the letter rather than dragging it right and down.
        container.addChild(shadow, glow, glyph)

        return { container, glyph, glow, shadow, width, baseline }
    }

    calcWidth = () => this.totalWidth

    /** Where the hairline under the letters sits, in the wordmark's own coordinates. */
    ruleY = () => this.lineHeight + RULE_GAP

    /**
     * A one-off flare over the resting halo. `intro.ts` fires it as the block starts sliding up, so
     * the glow and the motion read as one event rather than two things that happen to overlap.
     */
    pulse = () => {
        this.pulsedAt = Date.now()
    }

    /**
     * Lay the wordmark out and start it. `callback` fires once the letters have landed -- that is
     * the screen's cue to bring in the login card (and, without a flight, to slide the block up).
     *
     * With `flight`, the letters start where the loading screen drew its own small wordmark and fly
     * up into place, so the two screens share one "samsara" instead of crossfading between two.
     */
    update = (callback: Callback<void>, flight?: FlightOptions) => {
        const root = new Container()

        let x = 0
        this.letters.forEach((letter, i) => {
            this.restX[i] = x + this.letterWidths[i] / 2
            letter.container.x = this.restX[i]
            letter.container.y = this.lineHeight / 2
            root.addChild(letter.container)
            x += this.letterWidths[i] + TRACKING
        })

        this.rule = new Graphics()
        root.addChild(this.rule)

        if (flight && flight.from.length === this.letters.length) {
            this.flight = {
                from: flight.from.map((start, i) => {
                    const scale = start.size / LOGO_SIZE
                    return { x: start.x, y: start.baseline - this.letters[i].baseline * scale, scale }
                }),
                onLiftOff: flight.onLiftOff,
                liftedOff: false,
            }
            // The letters start at well under half size: let the GPU sample the big raster down
            // properly instead of skipping texels (WebGL2; WebGL1 ignores this for these sizes).
            this.letters.forEach(letter => (letter.glyph.texture.baseTexture.mipmap = MIPMAP_MODES.ON))
        }

        this.startedAt = Date.now()
        this.applyFrame(0)

        // The letters are driven by animation frames, but the CUE is a timer. A hidden tab gets no
        // frames, and a player who opens the game in a background tab still has to find a login
        // card waiting when they come back -- so the cue never depends on anything having rendered.
        const settleMs = this.flight ? realMs(flightSettleTs(this.letters.length)) : settleTs(this.letters.length)
        this.settleHandle = setTimeout(() => {
            this.liftOff()
            callback()
        }, settleMs)
        this.frameHandle = requestAnimationFrame(this.tick)

        return root
    }

    private tick = () => {
        if (this.disposed) {
            return
        }
        this.applyFrame(Date.now() - this.startedAt)
        this.frameHandle = requestAnimationFrame(this.tick)
    }

    private applyFrame = (elapsed: number) => {
        if (this.flight) {
            this.applyFlight(choreoMs(elapsed))
            return
        }
        const pulseElapsed = this.pulsedAt === undefined ? undefined : Date.now() - this.pulsedAt

        this.letters.forEach((letter, i) => {
            const state = letterStateAt(i, elapsed, pulseElapsed)
            letter.container.y = this.lineHeight / 2 + state.y
            letter.container.alpha = state.alpha
            letter.container.scale.set(state.scale)
            letter.glyph.tint = rampColor(COLORS.accent, COLORS.accentHi, COLORS.text, state.tintProgress)
            letter.glow.alpha = state.glowAlpha
        })

        this.drawRule(ruleProgressAt(elapsed, this.letters.length))
    }

    /** One frame of the hand-off flight; `t` is in choreography ms (see intro_pace). */
    private applyFlight = (t: number) => {
        const flight = this.flight!
        if (t >= FLIGHT_DELAY) {
            this.liftOff()
        }
        const pulseElapsed = this.pulsedAt === undefined ? undefined : choreoMs(Date.now() - this.pulsedAt)
        const restY = this.lineHeight / 2

        this.letters.forEach((letter, i) => {
            const state = flightStateAt(i, t, pulseElapsed)
            const from = flight.from[i]
            letter.container.x = from.x + (this.restX[i] - from.x) * state.travel
            letter.container.y = from.y + (restY - from.y) * state.travel
            letter.container.scale.set((from.scale + (1 - from.scale) * state.travel) * state.bump)
            letter.container.alpha = 1
            letter.glyph.tint = rampColor(LOADING_CREAM, COLORS.accentHi, COLORS.text, state.tintProgress)
            letter.glow.alpha = state.glowAlpha
            letter.shadow.alpha = SHADOW_ALPHA * state.shadow
        })

        this.drawRule(flightRuleProgressAt(t, this.letters.length))
    }

    private liftOff = () => {
        const flight = this.flight
        if (!flight || flight.liftedOff) {
            return
        }
        flight.liftedOff = true
        flight.onLiftOff?.()
    }

    /**
     * The hairline wipes out from the centre.
     *
     * It is one lens-shaped path, not a bar and not a row of segments. A bar has two hard stops
     * and reads as a divider; segments with falling alpha leave a bright seam everywhere two of
     * them overlap. A lens tapers to nothing at both ends in a single fill, which is also the
     * cheaper draw -- and the same "shape as one continuous path" rule the chat bubbles follow.
     */
    private drawRule = (progress: number) => {
        if (!this.rule) {
            return
        }

        this.rule.clear()
        if (progress <= 0) {
            return
        }

        const halfWidth = (easeOutExpo(progress) * this.totalWidth * RULE_WIDTH_RATIO) / 2
        const alpha = easeOutCubic(progress)
        const cx = this.totalWidth / 2
        const y = this.lineHeight + RULE_GAP

        // A quadratic's peak sits halfway to its control point, so the control goes at twice the
        // half-height we actually want.
        const lens = (half: number) => {
            this.rule.moveTo(cx - halfWidth, y)
            this.rule.quadraticCurveTo(cx, y - half * 2, cx + halfWidth, y)
            this.rule.quadraticCurveTo(cx, y + half * 2, cx - halfWidth, y)
            this.rule.closePath()
        }

        // Fatter, dimmer copy for the glow, then the hairline itself. Never a filter -- intro_theme.
        this.rule.beginFill(COLORS.accent, 0.14 * alpha)
        lens(RULE_GLOW_HALF)
        this.rule.endFill()

        this.rule.beginFill(COLORS.accentHi, 0.9 * alpha)
        lens(RULE_CORE_HALF)
        this.rule.endFill()
    }

    /**
     * Stop the loop and free the rasters. A pixi Text's canvas texture is not reclaimed by GC, and
     * the login screen is torn down on every sign-in, so this has to be called -- see
     * `Intro.guestLandingRemover`.
     */
    dispose = () => {
        this.disposed = true
        if (this.frameHandle !== undefined) {
            cancelAnimationFrame(this.frameHandle)
            this.frameHandle = undefined
        }
        if (this.settleHandle !== undefined) {
            clearTimeout(this.settleHandle)
            this.settleHandle = undefined
        }

        this.letters.forEach(letter => {
            // The sprites borrow the glyph's texture, so only the glyph may destroy it.
            letter.shadow.destroy({ texture: false, baseTexture: false })
            letter.glow.destroy({ texture: false, baseTexture: false })
            letter.glyph.destroy({ texture: true, baseTexture: true })
            letter.container.destroy({ children: true })
        })
        this.letters = []

        this.rule?.destroy()
        this.rule = undefined
    }
}

